import React, { useEffect, useRef } from 'react';
import { Card, Typography, Alert, Row, Col } from 'antd';
import * as d3 from 'd3';
import { CHART_DIMENSIONS, NODE_STYLES, LINK_STYLES, D3_FORCE_CONFIG } from '../config/chartStyles';

const { Title, Paragraph } = Typography;

interface Node {
  id: string;
  name: string;
  type: 'entity' | 'valueObject' | 'service' | 'repository';
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link {
  source: string;
  target: string;
  relationship: string;
}

const EntityRelationshipView: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);

  const nodes: Node[] = [
    { id: 'customer', name: 'Customer\n客户', type: 'entity' },
    { id: 'shipment', name: 'Shipment\n运输单', type: 'entity' },
    { id: 'cargo', name: 'Cargo\n货物', type: 'entity' },
    { id: 'address', name: 'Address\n地址', type: 'valueObject' },
    { id: 'money', name: 'Money\n金额', type: 'valueObject' },
    { id: 'dimensions', name: 'Dimensions\n尺寸', type: 'valueObject' },
    { id: 'route', name: 'Route\n路线', type: 'valueObject' },
    { id: 'freightService', name: '运费\n计算\nService', type: 'service' },
    { id: 'shipmentRepo', name: '运输\n数据\nRepo', type: 'repository' }
  ];

  const links: Link[] = [
    { source: 'shipment', target: 'customer', relationship: '关联' },
    { source: 'shipment', target: 'cargo', relationship: '包含' },
    { source: 'shipment', target: 'route', relationship: '路线' },
    { source: 'customer', target: 'address', relationship: '地址' },
    { source: 'cargo', target: 'dimensions', relationship: '尺寸' },
    { source: 'cargo', target: 'money', relationship: '价值' },
    { source: 'freightService', target: 'shipment', relationship: '计算' },
    { source: 'shipmentRepo', target: 'shipment', relationship: '存储' },
    { source: 'route', target: 'address', relationship: '起止点' }
  ];

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 1000;
    const height = 700;
    const margin = { top: 40, right: 40, bottom: 40, left: 40 };

    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const container = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const simulation = d3.forceSimulation(nodes as any)
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(180))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('center', d3.forceCenter((width - margin.left - margin.right) / 2, (height - margin.top - margin.bottom) / 2))
      .force('collision', d3.forceCollide().radius(90))
      .force('x', d3.forceX((width - margin.left - margin.right) / 2).strength(0.05))
      .force('y', d3.forceY((height - margin.top - margin.bottom) / 2).strength(0.05));

    const getNodeColor = (type: string) => {
      switch (type) {
        case 'entity': return '#1890ff';
        case 'valueObject': return '#52c41a';
        case 'service': return '#fa8c16';
        case 'repository': return '#eb2f96';
        default: return '#666';
      }
    };

    const link = container.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2);

    // 创建链接标签组
    const linkLabelGroups = container.append('g')
      .selectAll('g')
      .data(links)
      .enter()
      .append('g');

    // 添加白色背景矩形
    linkLabelGroups.append('rect')
      .attr('fill', 'white')
      .attr('stroke', '#f0f0f0')
      .attr('stroke-width', 1)
      .attr('rx', 3)
      .attr('ry', 3);

    // 添加文字标签
    const linkLabels = linkLabelGroups.append('text')
      .attr('font-size', '11px')
      .attr('fill', '#666')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .text(d => d.relationship);

    const node = container.append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .style('cursor', 'pointer')
      .call(d3.drag<any, any>()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    node.append('circle')
      .attr('r', 60)
      .attr('fill', d => getNodeColor(d.type))
      .attr('stroke', '#fff')
      .attr('stroke-width', 3)
      .attr('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))');

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', '10px')
      .attr('fill', 'white')
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .selectAll('tspan')
      .data(d => d.name.split('\\n'))
      .enter()
      .append('tspan')
      .attr('x', 0)
      .attr('dy', (d, i, arr) => {
        if (arr.length === 2) {
          return i === 0 ? '-0.4em' : '1.0em';
        } else if (arr.length === 3) {
          return i === 0 ? '-0.8em' : (i === 1 ? '0.2em' : '1.0em');
        } else {
          return '0em';
        }
      })
      .text(d => d);

    simulation.on('tick', () => {
      // 限制节点在边界内
      nodes.forEach(d => {
        d.x = Math.max(70, Math.min(width - margin.left - margin.right - 70, d.x!));
        d.y = Math.max(70, Math.min(height - margin.top - margin.bottom - 70, d.y!));
      });

      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      // 更新标签组位置
      linkLabelGroups
        .attr('transform', (d: any) => {
          const x = (d.source.x + d.target.x) / 2;
          const y = (d.source.y + d.target.y) / 2;
          return `translate(${x}, ${y})`;
        });

      // 更新标签文字位置
      linkLabels
        .attr('x', 0)
        .attr('y', 4);

      // 更新背景矩形大小和位置
      linkLabelGroups.each(function(this: any, d: any) {
        const group = d3.select(this);
        const textNode = group.select('text').node() as SVGTextElement | null;
        if (textNode) {
          const bbox = textNode.getBBox();
          group.select('rect')
            .attr('x', bbox.x - 4)
            .attr('y', bbox.y - 2)
            .attr('width', bbox.width + 8)
            .attr('height', bbox.height + 4);
        }
      });

      node
        .attr('transform', (d: any) => `translate(${d.x}, ${d.y})`);
    });

  }, []);

  return (
    <div>
      <Title level={2}>实体关系图</Title>
      <Paragraph>
        这个图表展示了货代业务中各个DDD组件之间的关系。不同颜色代表不同类型的组件，连线表示它们之间的关系。
        您可以拖动节点来重新排列布局。
      </Paragraph>

      <Alert
        message="图例说明"
        description={
          <Row gutter={[16, 8]}>
            <Col span={6}>🔵 蓝色 - 实体 (Entity)</Col>
            <Col span={6}>🟢 绿色 - 值对象 (Value Object)</Col>
            <Col span={6}>🟠 橙色 - 领域服务 (Domain Service)</Col>
            <Col span={6}>🔴 红色 - 数据库访问层 (Repository)</Col>
          </Row>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          width: '100%',
          overflowX: 'auto',
          padding: '10px'
        }}>
          <svg ref={svgRef} style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: '6px',
            background: '#fafafa',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}></svg>
        </div>
      </Card>

      <Card title="关系说明" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <h4>聚合关系</h4>
            <ul>
              <li><strong>Shipment</strong> 是聚合根，包含 Cargo 和引用 Customer</li>
              <li><strong>Customer</strong> 拥有 Address 值对象</li>
              <li><strong>Cargo</strong> 包含 Dimensions 和 Money 值对象</li>
            </ul>
          </Col>
          <Col xs={24} md={12}>
            <h4>服务关系</h4>
            <ul>
              <li><strong>FreightCalculationService</strong> 为 Shipment 计算费用</li>
              <li><strong>ShipmentRepository</strong> 负责 Shipment 的持久化</li>
              <li><strong>Route</strong> 连接起始和目的地 Address</li>
            </ul>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default EntityRelationshipView;